import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Col, Image, Row, Stack } from "react-bootstrap";
import LOGIN_BANNER from "../../assets/images/login-banner.svg";
import LOGO_WHITE from "../../assets/images/logo.svg";
import { regenerate2faAPI, verify2faAPI } from "../../services/auth.service";
import { ForgotSchema } from "./validation";
import storage from "../../helpers/storage";
import sessionStorage from "../../helpers/sessionStorage";
import { toast } from "react-toastify";
import CustomInputText from "../../components/common/CustomInputText";
import CommonFormikComponent from "../../components/common/CommonFormikComponent";
import Loader from "../../components/common/loader";

type FormValues = {
  email: string;
  code: string;
};

export default function TwoFactorAuthentication() {
  const AppName = process.env.REACT_APP_NAME;

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const location = useLocation();
  const { email } = location.state;
  console.log("queryEmail = ", email);
  const [isMounted, setIsMounted] = useState(false);

  /**
   * Initial Form Values
   */
  const initialValues: FormValues = {
    email: email || "",
    code: "",
  };

  /**
   * component mounted hook
   */
  useEffect(() => {
    // This code will run when the component is mounted
    setIsMounted(true);
    // Define cleanup logic here
    return () => {
      if (isMounted) {
        sessionStorage.delete("2fa");
      }
    };
  }, [isMounted]);

  /**
   * Check login state and email
   */
  useEffect(() => {
    if (storage.getToken() != null) {
      return navigate("/home");
    }
    const twoFAEmail = sessionStorage.getData("2fa");
    if (twoFAEmail !== email) {
      toast("Unauthorized page access.", { type: toast.TYPE.ERROR });
      navigate("/login");
    }
  }, [navigate, email]);

  /**
   * Submit Method to call when user clicks on verify button
   * @async
   * @param {email: string, token: string} values
   * @returns {*}
   */
  const handleSubmit = async (
    values: Record<string, any>,
    { setSubmitting }: any,
  ) => {
    setLoading(true);
    setSubmitting(true);
    verify2faAPI({ email: values.email, code: values.code })
      .then((response: any) => {
        toast(response.message, { type: toast.TYPE.SUCCESS });
        setLoading(false);
        setSubmitting(false);
        sessionStorage.delete("2fa");
        storage.setToken(response.data.token.access.token);
        storage.setRefreshToken(response.data.token.refresh.token);
        navigate("/dashboard");
      })
      .catch((error: any) => {
        setLoading(false);
        setSubmitting(false);
        toast(error.data.message, { type: toast.TYPE.ERROR });
      });
  };

  const resendCode = () => {
    setRegenerating(true);
    regenerate2faAPI({ email })
      .then((response: any) => {
        console.log(response);
        setRegenerating(false);
        toast(response.message, { type: toast.TYPE.SUCCESS });
      })
      .catch((error: any) => {
        console.log(error);
        setRegenerating(false);
        toast(error.data.message, { type: toast.TYPE.ERROR });
      });
  };

  return (
    <React.Fragment>
      <Loader isLoading={loading} />
      <Row className="g-0 vh-100">
        <Col md={6}>
          <Stack
            className="custom-primary-gradient text-center justify-content-around h-100 p-4"
            gap={4}
          >
            <div className="user-select-none">
              <Image
                fluid={true}
                src={LOGO_WHITE}
                alt={`${AppName} Logo`}
                width={158}
                height={48}
              />
            </div>
            <div>
              <Image
                fluid={true}
                src={LOGIN_BANNER}
                alt={`${AppName} Customer Panel`}
                width={389}
                height={334}
              />
            </div>
            <div className="lh-sm text-white">
              <div className="fw-light fs-4">Advanced Dynamic</div>
              <div className="fw-semibold fs-1 text-uppercase">
                Vulnerability Scanning
              </div>
              <div className="fw-light fs-4">for Enterprises</div>
            </div>
          </Stack>
        </Col>
        <Col md={6} className="col-md-6 d-flex flex-column p-3">
          <div className="custom-width-320 d-flex flex-column flex-grow-1 mw-100 mx-auto py-3">
            <div className="my-auto py-4">
              <p className="mb-1 opacity-75 text-uppercase">
                Two Factor Authentication
              </p>
              <h1 className="fw-semibold mb-4 pb-1 fs-2">Customer Panel</h1>
              <CommonFormikComponent
                validationSchema={ForgotSchema}
                initialValues={initialValues}
                onSubmit={handleSubmit}
              >
                {(formikProps) => (
                  <>
                    <CustomInputText
                      wrapperClassname="mb-3 pb-1"
                      controlId="code"
                      label="Code"
                      placeholder="4 digit code"
                      touched={formikProps.touched.code}
                      handleBlur={formikProps.handleBlur}
                      handleChange={formikProps.handleChange}
                      errorsField={formikProps.errors.code}
                      value={formikProps.values.code}
                      validationSchema={ForgotSchema}
                    />

                    <Stack
                      direction="horizontal"
                      gap={3}
                      className="justify-content-end"
                    >
                      <Button
                        className="w-100"
                        variant="primary"
                        type="submit"
                        disabled={formikProps.isSubmitting || loading}
                      >
                        {loading ? "Verifying..." : "Verify"}
                      </Button>
                      <Button
                        className="w-100"
                        variant="primary"
                        type="button"
                        onClick={resendCode}
                        disabled={regenerating}
                      >
                        Resend Code
                      </Button>
                    </Stack>
                  </>
                )}
              </CommonFormikComponent>
            </div>
          </div>
        </Col>
      </Row>
    </React.Fragment>
  );
}
